/**
 * VedAstro API client (https://vedastro.org)
 *
 * A small, isolated fetch wrapper for the public VedAstro Vedic-astrology REST
 * API. This talks to a third-party service directly from the browser (the API
 * sends `Access-Control-Allow-Origin: *`), so it deliberately does NOT go
 * through the app's own BFF axios client.
 *
 * Auth: an optional premium key removes the free tier's 5-calls/min limit.
 * Set `VITE_VEDASTRO_API_KEY` in the website's env to enable it. Without a key
 * the free tier is used, which is rate-limited per IP.
 */

export const BASE = 'https://api.vedastro.org/api/Calculate'
export const AYANAMSA = 'LAHIRI'
const DEFAULT_TZ = '+05:30' // Indian Standard Time — the primary audience

const API_KEY =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_VEDASTRO_API_KEY) || ''

export function authHeaders() {
  const h = { 'Content-Type': 'application/json' }
  if (API_KEY) h['x-api-key'] = API_KEY
  return h
}

/** Friendly, throwable error the UI can show as-is. */
export class VedAstroError extends Error {
  constructor(message, { rateLimited = false } = {}) {
    super(message)
    this.name = 'VedAstroError'
    this.rateLimited = rateLimited
  }
}

/**
 * POST a Calculate method. VedAstro replies `{ Status, Payload }`; on success
 * the Payload is an object keyed by the method name — we unwrap that single key.
 */
export async function callMethod(method, params, signal) {
  let res
  try {
    res = await fetch(`${BASE}/${method}`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ ...params, Ayanamsa: AYANAMSA }),
      signal,
    })
  } catch (err) {
    if (err?.name === 'AbortError') throw err
    throw new VedAstroError('Could not reach the astrology service. Check your connection and try again.')
  }

  if (res.status === 429) {
    throw new VedAstroError('The free astrology service is busy right now. Please wait a moment and try again.', {
      rateLimited: true,
    })
  }
  if (!res.ok) throw new VedAstroError(`The astrology service returned an error (${res.status}). Please try again.`)

  let json
  try {
    json = await res.json()
  } catch {
    throw new VedAstroError('Received an unexpected response from the astrology service.')
  }

  if (json?.Status !== 'Pass') {
    const msg = typeof json?.Payload === 'string' ? json.Payload : 'The astrology service could not complete the calculation.'
    throw new VedAstroError(msg)
  }

  const payload = json.Payload
  // Payload is `{ MethodName: value }` — unwrap when that's the shape.
  if (payload && typeof payload === 'object' && !Array.isArray(payload) && method in payload) {
    return payload[method]
  }
  return payload
}

/** Build the VedAstro `Time` object from form fields. */
function buildTime(dateStr, timeStr, location, tz = DEFAULT_TZ) {
  const [y, m, d] = String(dateStr).split('-')
  const hm = /^\d{1,2}:\d{2}/.test(timeStr || '') ? timeStr : '12:00'
  return { StdTime: `${hm} ${d}/${m}/${y} ${tz}`, Location: location }
}

/** Pull "HH:MM AM/PM" out of a VedAstro StdTime like "06:39 25/10/1992 +05:30". */
function formatStdTime(std) {
  const raw = (std || '').split(' ')[0]
  const [hStr, mStr] = raw.split(':')
  let h = Number(hStr)
  const mm = mStr ?? '00'
  const suffix = h >= 12 ? 'PM' : 'AM'
  const h12 = ((h + 11) % 12) + 1
  return `${h12}:${mm} ${suffix}`
}

/** Decimal hours (e.g. 6.65) from a StdTime string. */
function stdTimeToHours(std) {
  const raw = (std || '').split(' ')[0]
  const [h, m] = raw.split(':').map(Number)
  return (h || 0) + (m || 0) / 60
}

/** Decimal hours → "H:MM AM/PM". */
function hoursToLabel(dec) {
  let h = Math.floor(dec)
  let mm = Math.round((dec - h) * 60)
  if (mm === 60) { mm = 0; h += 1 }
  const suffix = h >= 12 ? 'PM' : 'AM'
  const h12 = ((h + 11) % 12) + 1
  return `${h12}:${String(mm).padStart(2, '0')} ${suffix}`
}

// Which of the 8 daylight parts is Rahu Kalam, by weekday (1-indexed).
const RAHU_PART = { Sunday: 8, Monday: 2, Tuesday: 7, Wednesday: 5, Thursday: 6, Friday: 4, Saturday: 3 }

/** Classic Rahu Kalam window: daylight split into 8 parts, the weekday's part. */
function rahuKalam(vara, sunriseStd, sunsetStd) {
  const sr = stdTimeToHours(sunriseStd)
  const ss = stdTimeToHours(sunsetStd)
  const part = RAHU_PART[vara]
  if (!part || !(ss > sr)) return null
  const dur = (ss - sr) / 8
  const start = sr + (part - 1) * dur
  return `${hoursToLabel(start)} - ${hoursToLabel(start + dur)}`
}

/* ─────────────────────────── Location search ─────────────────────────── */

// Location search is a separate controller from the `Calculate` reflection
// dispatcher `BASE` points at — `Calculate/SearchLocation/...` 404s with
// "Calculator method not found!" since SearchLocation isn't one of the ~680
// calculator methods. Verified live 2026-09-11.
const GEO_BASE = 'https://api.vedastro.org/api/GeoLocation'

const NEW_DELHI = { Name: 'New Delhi, India', Longitude: 77.209, Latitude: 28.6139 }

/**
 * Geocode a free-text place via VedAstro's SearchLocation (GET). Returns
 * `{ Name, Longitude, Latitude }`. Falls back to New Delhi when the place is
 * blank or unrecognised so downstream calculations never fail.
 */
export async function resolveLocation(address, signal) {
  const q = (address || '').trim()
  if (!q) return NEW_DELHI
  try {
    const res = await fetch(`${GEO_BASE}/SearchLocation/Address/${encodeURIComponent(q)}`, { signal })
    const json = await res.json()
    const list = json?.Payload?.SearchLocation
    if (Array.isArray(list) && list.length) {
      const { Name, Longitude, Latitude } = list[0]
      return { Name, Longitude, Latitude }
    }
  } catch (err) {
    if (err?.name === 'AbortError') throw err
  }
  return NEW_DELHI
}

/**
 * Typeahead location search — the same `SearchLocation` endpoint, but returns
 * the FULL suggestion list (up to ~10) instead of just the top hit, for an
 * autocomplete dropdown. Each item carries its own exact coordinates, so the
 * chosen place needs no second geocoding call. Sends the premium API key so
 * per-keystroke searches don't hit the free tier's rate limit.
 * Returns `[{ Name, Longitude, Latitude }]` (empty on error/blank).
 */
export async function searchLocations(query, signal) {
  const q = (query || '').trim()
  if (!q) return []
  try {
    const res = await fetch(`${GEO_BASE}/SearchLocation/Address/${encodeURIComponent(q)}`, {
      headers: authHeaders(),
      signal,
    })
    const json = await res.json()
    const list = json?.Payload?.SearchLocation
    if (Array.isArray(list)) {
      return list.map(({ Name, Longitude, Latitude }) => ({ Name, Longitude, Latitude }))
    }
  } catch (err) {
    if (err?.name === 'AbortError') throw err
  }
  return []
}

/* ─────────────────────────── Kundli (birth chart) ─────────────────────────── */

// Sidereal sign order (Aries … Pisces) — used to derive Ketu (opposite Rahu).
export const SIGN_ORDER = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces',
]

/** The sign exactly opposite (180°) a given sign — Ketu always faces Rahu. */
function oppositeSign(sign) {
  const i = SIGN_ORDER.indexOf(sign)
  return i < 0 ? null : SIGN_ORDER[(i + 6) % 12]
}

// The 8 physical/shadow grahas VedAstro can place directly; Ketu is derived.
const KUNDLI_PLANETS = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu']

/**
 * Reduce a `DasaForNow` payload (`{ Maha: { SubDasas: { Bhukti: { SubDasas: … } } } }`)
 * to the currently-running lords at each of the top three levels.
 */
function parseDasa(payload) {
  if (!payload || typeof payload !== 'object') return null
  const maha = Object.keys(payload)[0]
  if (!maha) return null
  const mahaObj = payload[maha] || {}
  const bhukti = mahaObj.SubDasas ? Object.keys(mahaObj.SubDasas)[0] : null
  const bhuktiObj = bhukti ? mahaObj.SubDasas[bhukti] : {}
  const antaram = bhuktiObj?.SubDasas ? Object.keys(bhuktiObj.SubDasas)[0] : null
  return { maha, bhukti, antaram }
}

/**
 * Full Vedic birth chart (Janma Kundli) from the Swiss-Ephemeris-backed engine:
 * Lagna (ascendant), the sidereal sign + degree of all nine grahas (Ketu derived
 * opposite Rahu), the birth Nakshatra + pada, and the currently-running Dasa.
 * House placements are derived locally from the Lagna by the caller.
 */
export async function getKundli({ dob, tob, location }, signal) {
  const time = buildTime(dob, tob, location)
  const [lagna, moonConstellation, dasaPayload, ...planetSigns] = await Promise.all([
    callMethod('LagnaSignName', { time }, signal),
    callMethod('MoonConstellation', { time }, signal),
    // DasaForNow takes `birthTime` (not `time`); best-effort so it never blocks the chart.
    callMethod('DasaForNow', { birthTime: time }, signal).catch((err) => {
      if (err?.name === 'AbortError') throw err
      return null
    }),
    // PlanetName is an object param — a bare string is ignored by the API.
    ...KUNDLI_PLANETS.map((pl) =>
      callMethod('PlanetRasiD1Sign', { planetName: { Name: pl }, time }, signal),
    ),
  ])

  const planets = KUNDLI_PLANETS.map((name, i) => {
    const r = planetSigns[i]
    return { name, sign: r?.Name ?? r, deg: Number(r?.DegreesIn?.TotalDegrees) || 0 }
  })
  // Ketu sits exactly opposite Rahu — derive it rather than spend another call.
  const rahu = planets.find((p) => p.name === 'Rahu')
  if (rahu?.sign) planets.push({ name: 'Ketu', sign: oppositeSign(rahu.sign), deg: rahu.deg })

  const moon = planets.find((p) => p.name === 'Moon')
  const sun = planets.find((p) => p.name === 'Sun')
  // MoonConstellation looks like "Chitta - 4" → name + pada.
  const [nakName, padaRaw] = String(moonConstellation).split(' - ')

  return {
    lagna, // e.g. "Aquarius"
    moonSign: moon?.sign ?? null, // e.g. "Libra"
    sunSign: sun?.sign ?? null, // e.g. "Leo"
    nakshatra: (nakName || '').trim(),
    pada: (padaRaw || '').trim(),
    planets, // [{ name, sign, deg }] for all 9 grahas incl. Ketu
    dasa: parseDasa(dasaPayload), // { maha, bhukti, antaram } | null
    placeName: location.Name,
  }
}

/* ─────────────────────────── Panchang (almanac) ─────────────────────────── */

/**
 * Full Panchanga for a date/location in a single call, plus the Moon sign.
 * `location` defaults to New Delhi to match the tool's original behaviour.
 * Pass `time` ("HH:MM") to compute the panchanga at an exact moment — e.g.
 * the birth time for Avakhada details (tithi/yoga/karana at birth).
 */
export async function getPanchang({ date, time = '12:00', location = NEW_DELHI }, signal) {
  const inputTime = buildTime(date, time, location)
  const [table, moonSign] = await Promise.all([
    callMethod('PanchangaTable', { inputTime }, signal),
    callMethod('MoonSignName', { time: inputTime }, signal),
  ])

  return {
    tithi: table.Tithi, // { Name, Paksha, ... }
    nakshatra: table.Nakshatra, // "Chitta - 4"
    yoga: table.Yoga, // { Name, Description }
    karana: table.Karana, // "Chatushpada"
    vara: table.Vara, // "Sunday"
    horaLord: table.HoraLord?.Name,
    dishaShool: table.DishaShool,
    sunrise: formatStdTime(table.Sunrise?.StdTime),
    sunset: formatStdTime(table.Sunset?.StdTime),
    rahuKalam: rahuKalam(table.Vara, table.Sunrise?.StdTime, table.Sunset?.StdTime),
    moonSign,
    lunarMonth: table.LunarMonth,
  }
}

/* ─────────────────────────── Match / compatibility ─────────────────────────── */

/**
 * Kuta compatibility from VedAstro's MatchReport. Returns the overall score
 * (0–100), a summary verdict, and the full list of named factors so the caller
 * can render the classic Ashtakoota rows from real Good/Bad/Neutral ratings.
 */
export async function getMatchReport({ maleTime, femaleTime }, signal) {
  const report = await callMethod(
    'MatchReport',
    { maleBirthTime: maleTime, femaleBirthTime: femaleTime },
    signal,
  )
  return {
    kutaScore: report.KutaScore, // percentage 0–100
    factors: report.PredictionList || [], // [{ Name, Nature, Info, Description }]
    summary: report.Summary || {}, // { ScoreSummary, ScoreColor }
  }
}

/* ─────────────────────────── Horoscope predictions ─────────────────────────── */

/**
 * Full birth-chart prediction report (VedAstro `HoroscopePredictions` — the
 * same engine behind vedastro.org's Horoscope page). Returns ~100+ classical
 * text predictions, each tagged with life areas and the grahas/houses that
 * produce it: `[{ name, text, planets, houses, tags }]`.
 */
export async function getHoroscopePredictions({ dob, tob, location }, signal) {
  const birthTime = buildTime(dob, tob, location)
  const list = await callMethod('HoroscopePredictions', { birthTime }, signal)
  if (!Array.isArray(list)) return []
  return list
    .map((p) => ({
      name: p?.Name || '',
      text: String(p?.Description || '').trim(),
      planets: p?.RelatedBody?.Planets || [],
      houses: p?.RelatedBody?.Houses || [],
      tags: Array.isArray(p?.Tags) ? p.Tags : [],
    }))
    .filter((p) => p.text)
}

/* ─────────────────────────── Planet strength (Shadbala) ─────────────────────────── */

// Shadbala is defined for the seven classical grahas only (not Rahu/Ketu).
const SHADBALA_PLANETS = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn']

/* Classical minimum Shadbala Pinda (in Virupas) each graha needs to be
   counted strong — B.V. Raman's standard table, the same thresholds
   VedAstro's own IsPlanetStrongInShadbala applies server-side. */
export const REQUIRED_SHADBALA = { Sun: 390, Moon: 360, Mars: 300, Mercury: 420, Jupiter: 390, Venus: 330, Saturn: 300 }

/**
 * Shadbala strength + Ishta/Kashta (benefic/malefic tendency) scores for the
 * seven classical grahas — the classical basis for prescribing remedies.
 * Verified param shapes: PlanetShadbalaPinda takes `planetName`+`time`;
 * the Ishta/Kashta scores take `planet`+`birthTime`.
 *
 * Returns `[{ name, pinda, required, ratio, strong, ishta, kashta, afflicted }]`
 * — `afflicted` = weak in Shadbala or Kashta (malefic) outweighing Ishta.
 */
export async function getPlanetStrengths({ dob, tob, location }, signal) {
  const time = buildTime(dob, tob, location)
  const rows = await Promise.all(
    SHADBALA_PLANETS.map(async (name) => {
      const [pinda, ishta, kashta] = await Promise.all([
        callMethod('PlanetShadbalaPinda', { planetName: { Name: name }, time }, signal),
        callMethod('PlanetIshtaScore', { planet: { Name: name }, birthTime: time }, signal),
        callMethod('PlanetKashtaScore', { planet: { Name: name }, birthTime: time }, signal),
      ])
      const pindaNum = Number(pinda) || 0
      const ishtaNum = Number(ishta) || 0
      const kashtaNum = Number(kashta) || 0
      const required = REQUIRED_SHADBALA[name]
      const strong = pindaNum >= required
      return {
        name,
        pinda: pindaNum,
        required,
        ratio: required ? pindaNum / required : 0,
        strong,
        ishta: ishtaNum,
        kashta: kashtaNum,
        afflicted: !strong || kashtaNum > ishtaNum,
      }
    }),
  )
  return rows
}

/* ─────────────────────────── Transits (Gochara) ─────────────────────────── */

// Fast + major movers used for a Vedic transit (Gochara) reading.
const TRANSIT_PLANETS = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu']

/**
 * The sidereal sign (and degree) each planet is transiting on a given date,
 * computed for New Delhi noon. Transit positions are absolute — the same for
 * every zodiac sign — so a caller fetches once per date and derives each sign's
 * house positions locally.
 *
 * Returns `{ Sun: { sign, deg }, Moon: { sign, deg }, ... }`.
 */
export async function getPlanetTransits(dateStr, signal) {
  const time = buildTime(dateStr, '12:00', NEW_DELHI)
  const entries = await Promise.all(
    TRANSIT_PLANETS.map(async (pl) => {
      const r = await callMethod('PlanetRasiD1Sign', { planetName: { Name: pl }, time }, signal)
      const sign = r?.Name ?? r
      const deg = Number(r?.DegreesIn?.TotalDegrees) || 0
      return [pl, { sign, deg }]
    }),
  )
  return Object.fromEntries(entries)
}

export { buildTime, NEW_DELHI }
